import { Injectable, signal } from '@angular/core';

/**
 * Loading state service
 * Manages global loading indicators
 * Uses Angular signals for reactive state
 * 
 * Usage:
 * ```
 * constructor(private loadingService: LoadingService) {}
 * 
 * // Show loading
 * this.loadingService.show();
 * 
 * // Hide loading
 * this.loadingService.hide();
 * 
 * // Check loading state
 * if (this.loadingService.isLoading()) {
 *   // Show spinner
 * }
 * 
 * // Get active requests count
 * const count = this.loadingService.activeRequestsCount();
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class LoadingService {
  private activeRequestsSignal = signal<number>(0);
  private manualLoadingSignal = signal<boolean>(false);

  /**
   * Computed signal for loading state
   * Returns true if there are active requests or manual loading is set
   */
  readonly isLoading = signal<boolean>(false);

  /**
   * Active HTTP requests count (readonly)
   */
  readonly activeRequestsCount = this.activeRequestsSignal.asReadonly();

  constructor() {
    // Update isLoading based on active requests or manual loading
    this.updateLoadingState();
  }

  /**
   * Show loading indicator (manual control)
   */
  show(): void {
    this.manualLoadingSignal.set(true);
    this.updateLoadingState();
  }

  /**
   * Hide loading indicator (manual control)
   */
  hide(): void {
    this.manualLoadingSignal.set(false);
    this.updateLoadingState();
  }

  /**
   * Increment active requests count
   * Called by loading interceptor when a request starts
   */
  incrementRequests(): void {
    this.activeRequestsSignal.update(count => count + 1);
    this.updateLoadingState();
  }

  /**
   * Decrement active requests count
   * Called by loading interceptor when a request completes
   */
  decrementRequests(): void {
    this.activeRequestsSignal.update(count => Math.max(0, count - 1));
    this.updateLoadingState();
  }

  /**
   * Reset all loading states
   */
  reset(): void {
    this.activeRequestsSignal.set(0);
    this.manualLoadingSignal.set(false);
    this.updateLoadingState();
  }

  /**
   * Update the loading state based on active requests and manual loading
   */
  private updateLoadingState(): void {
    const hasActiveRequests = this.activeRequestsSignal() > 0;
    const isManuallyLoading = this.manualLoadingSignal();
    this.isLoading.set(hasActiveRequests || isManuallyLoading);
  }

  /**
   * Execute a function with loading indicator
   * Automatically shows/hides loading before and after execution
   * 
   * Usage:
   * ```
   * await this.loadingService.withLoading(async () => {
   *   await this.someAsyncOperation();
   * });
   * ```
   */
  async withLoading<T>(fn: () => Promise<T>): Promise<T> {
    this.show();
    try {
      return await fn();
    } finally {
      this.hide();
    }
  }
}


